import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { generateReportPDF } from "@/knowmeno/KnowMenoApp";
import { localDateKey } from "@/knowmeno/dailyLogs";
import type { ReportData, HotFlashEvent } from "@/knowmeno/reportData";

// ============================================================
// DEV TOOL — Sample Symptom Intelligence Report
// ============================================================
// Generates the EXACT PDF a real user would receive, populated
// with a rich 143-day synthetic dataset for landing-page work.
// Anchors to today() so dates roll forward each invocation —
// that's fine for marketing capture.
//
// Gating (belt + braces):
//   1. DEV_TOOLS_ENABLED constant below MUST be true
//   2. Signed-in email must match the founder allowlist
//
// To disable: flip DEV_TOOLS_ENABLED to false (one line). Route
// then returns 403 for everyone, regardless of email.
// ============================================================

const DEV_TOOLS_ENABLED = true;

const FOUNDER_EMAIL_RE = /^founders(\+[^@]+)?@(knowmeno|meno-iq)\.com$/i;

// ============================================================
// SYNTHETIC DATASET — "Sarah M.", late perimenopause
// ============================================================
// Tracking window: 143 days (daysAgo 0..142).
// HRT start day: daysAgo 112 (= day 31 of tracking, "~30 days in").
// Skip day: daysAgo 15 only → 142 logged days in window,
//           89 of last 90 logged → HIGH confidence target.
//
// Phase severity targets (per symptom) drive the three key
// outputs:
//   • Last-30 averages match spec
//   • HRT response (30 days before vs 30 days after HRT start)
//     fires "Mood swings worsened" as the worst responder
//   • Mood swings lastSevere ≥ 10 in last-30 fires the
//     "X severe days in last 30d" medium flag
// ============================================================

// HRT_START_DAYS_AGO removed: hrtStartDate now anchored to keyDaysAgo(30)
// inline below to align with the hot-flash event narrative (Phase 2/3).
const TRACKING_DAYS = 143;
const SKIP_DAYS_AGO = new Set<number>([15]); // single skip day to land 89/90

type Phase = "last30" | "prior30" | "middle" | "post" | "pre";

// Phase windows (by daysAgo, today = 0, oldest = 142). Each window
// maps 1:1 to a specific renderer so bucket counts can be sized
// exactly and no day-entries get dropped:
//   last30:  0..29    ← buildSymptomDirections last-30 + Daily Averages
//   prior30: 30..59   ← buildSymptomDirections prior-30 window
//   middle:  60..81   ← heatmap density filler (no metric reads this)
//   post:    82..111  ← buildHrtResponse POST window
//   skip:    112      ← HRT start day; buildHrtResponse excludes it
//   pre:     113..142 ← buildHrtResponse PRE window
function phaseFor(daysAgo: number): Phase | "start" {
  if (daysAgo <= 29) return "last30";
  if (daysAgo <= 59) return "prior30";
  if (daysAgo <= 81) return "middle";
  if (daysAgo <= 111) return "post";
  if (daysAgo === 112) return "start";
  return "pre";
}

// Per-symptom, per-phase severity buckets.
// Each bucket is { sev: count }, summing to the phase day count
// (30 days for pre/post, 82 days for recent). The bucket positions
// are assigned deterministically via a small interleaver below so
// the heatmap looks varied, not striped.
//
// AVG-MATH VERIFICATION (last 30 = daysAgo 0..29 minus skip day 15
// = 29 days; prior 30 = daysAgo 30..59 = 30 days; middle 22 days;
// post 30; pre 30):
//
//   Hot flashes:   last30  29d → 1.897 avg, 5 severe  (spec 1.9)
//                  prior30 30d → 1.867 avg, 5 severe  (Δ +1.6% → stable)
//                  middle  22d → 1.27 avg (filler)
//                  post    30d → 1.50 avg              (HRT post)
//                  pre     30d → 3.00 avg              (HRT pre)
//   Night sweats:  last30  29d → 2.000 avg, 6 severe  (spec 2.0)
//                  prior30 30d → 2.000 avg, 6 severe  (Δ  0%  → stable)
//                  middle  22d → 1.27 avg (filler)
//                  post    30d → 1.40 avg
//                  pre     30d → 2.80 avg
//   Brain fog:     last30  29d → 2.276 avg, 9 severe  (spec 2.3)
//                  prior30 30d → 2.267 avg, 9 severe  (Δ +0.4% → stable)
//                  middle  22d → 1.27 avg (filler)
//                  post    30d → 1.60 avg
//                  pre     30d → 2.60 avg
//   Fatigue:       last30  29d → 2.103 avg, 8 severe  (spec 2.1)
//                  prior30 30d → 2.100 avg, 8 severe  (Δ +0.1% → stable)
//                  middle  22d → 1.27 avg (filler)
//                  post    30d → 1.60 avg
//                  pre     30d → 2.10 avg
//   Mood swings:   last30  29d → 2.690 avg, 20 severe (spec 2.7)
//                  prior30 30d → 2.667 avg, 20 severe (Δ +0.9% → stable)
//                  middle  22d → 1.27 avg (filler)
//                  post    30d → 2.80 avg
//                  pre     30d → 2.10 avg
//
// HRT Response (pre vs post) is UNCHANGED — post and pre buckets are
// byte-identical to the prior version, so all section deltas
// (hot flashes 3.0→1.5, mood swings 2.1→2.8, etc.) continue to render.
//
// Flag math (report renderer, KnowMenoApp.tsx:2648-2658):
//   • dir="stable" for every symptom (|pct| < 15) → no per-symptom
//     worsening flags
//   • Mood swings lastSevere = 20 ≥ 10 → MEDIUM "20 severe days" flag
//   • worstResponder = Mood swings at pct -33% → HIGH HRT non-response
type Bucket = { sev1: number; sev2: number; sev3: number; sev4?: number };

const PLAN: Record<string, Record<Phase, Bucket>> = {
  "Hot flashes":  {
    last30:  { sev1: 8,  sev2: 16, sev3: 5  }, // 29d, avg 1.897
    prior30: { sev1: 9,  sev2: 16, sev3: 5  }, // 30d, avg 1.867
    middle:  { sev1: 16, sev2: 6,  sev3: 0  }, // 22d, avg 1.27 (filler)
    post:    { sev1: 16, sev2: 13, sev3: 1  }, // 30d, avg 1.50
    pre:     { sev1: 0,  sev2: 3,  sev3: 24, sev4: 3 }, // 30d, avg 3.00
  },
  "Night sweats": {
    last30:  { sev1: 6,  sev2: 17, sev3: 6  }, // 29d, avg 2.000
    prior30: { sev1: 6,  sev2: 18, sev3: 6  }, // 30d, avg 2.000
    middle:  { sev1: 16, sev2: 6,  sev3: 0  }, // 22d, avg 1.27 (filler)
    post:    { sev1: 19, sev2: 10, sev3: 1  }, // 30d, avg 1.40
    pre:     { sev1: 0,  sev2: 9,  sev3: 18, sev4: 3 }, // 30d, avg 2.80
  },
  "Brain fog":    {
    last30:  { sev1: 1,  sev2: 19, sev3: 9  }, // 29d, avg 2.276
    prior30: { sev1: 1,  sev2: 20, sev3: 9  }, // 30d, avg 2.267
    middle:  { sev1: 16, sev2: 6,  sev3: 0  }, // 22d, avg 1.27 (filler)
    post:    { sev1: 14, sev2: 14, sev3: 2  }, // 30d, avg 1.60
    pre:     { sev1: 1,  sev2: 10, sev3: 19 }, // 30d, avg 2.60
  },
  "Fatigue":      {
    last30:  { sev1: 5,  sev2: 16, sev3: 8  }, // 29d, avg 2.103
    prior30: { sev1: 5,  sev2: 17, sev3: 8  }, // 30d, avg 2.100
    middle:  { sev1: 16, sev2: 6,  sev3: 0  }, // 22d, avg 1.27 (filler)
    post:    { sev1: 14, sev2: 14, sev3: 2  }, // 30d, avg 1.60
    pre:     { sev1: 5,  sev2: 17, sev3: 8  }, // 30d, avg 2.10
  },
  "Mood swings":  {
    last30:  { sev1: 0,  sev2: 9,  sev3: 20 }, // 29d, avg 2.690, 20 severe
    prior30: { sev1: 0,  sev2: 10, sev3: 20 }, // 30d, avg 2.667, 20 severe
    middle:  { sev1: 16, sev2: 6,  sev3: 0  }, // 22d, avg 1.27 (filler)
    post:    { sev1: 0,  sev2: 6,  sev3: 24 }, // 30d, avg 2.80
    pre:     { sev1: 5,  sev2: 17, sev3: 8  }, // 30d, avg 2.10
  },
};

// Deterministic per-symptom interleaver: given the bucket for a
// phase, returns a sev value for the Nth logged day in that phase.
// We rotate through [3,2,1,2,3,2,1,2,...] families per symptom to
// keep the heatmap visually varied while honoring exact counts.
function planSequence(bucket: Bucket, offset: number): number[] {
  const flat: number[] = [];
  // High → low gives a "settled mid week, worse weekend" feel by mod-7.
  for (let i = 0; i < (bucket.sev4 || 0); i++) flat.push(4);
  for (let i = 0; i < bucket.sev3; i++) flat.push(3);
  for (let i = 0; i < bucket.sev2; i++) flat.push(2);
  for (let i = 0; i < bucket.sev1; i++) flat.push(1);
  // Interleave by mod-3 rotation so adjacent days aren't identical.
  const N = flat.length;
  const out: number[] = new Array(N);
  for (let i = 0; i < N; i++) {
    const src = ((i * 3 + offset) % N + N) % N;
    out[i] = flat[src];
  }
  return out;
}

type MockBundle = {
  user: Record<string, unknown>;
  cycles: Array<{ start: string; cycle: number; days: number; flow: string }>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  labs: any[];
  questions: string[];
  reportData: ReportData;
  hrtByDate: Record<string, boolean>;
  hrtDates90: string[];
  moodRows: { log_date: string; mood: number }[];
  hotFlashEvents: HotFlashEvent[];
};

// ============================================================
// Seeded RNG + Sarah M. hot-flash event generator
// ============================================================
// Deterministic mulberry32 PRNG seeded from a stable string so
// /dev/sample-report renders identical numbers on every reload.
// Generates 90 days of evening-skewed hot-flash events with a
// sharper-than-linear HRT decay narrative:
//   Phase 1 (daysAgo 60..89, pre-HRT):     ~664 events (~22/day)
//   Phase 2 (daysAgo 30..59, on HRT decay): ~58 events ((1-i/30)^1.6)
//   Phase 3 (daysAgo  0..29, settled):       8 events (sparse)
// Last-30 vs prior-30 lands at ~-86% → "improving" headline,
// inside the audited 75-90% band.
function mulberry32(seed: number) {
  return function () {
    let t = (seed += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function stringSeed(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return h >>> 0;
}
// New York timezone offset helper — keeps event timestamps anchored
// to America/New_York local "hour of day" regardless of where the
// dev tool is opened. Returns ms offset from UTC for the given date.
function nyOffsetMs(d: Date): number {
  const utc = d.getTime();
  const tzDate = new Date(d.toLocaleString("en-US", { timeZone: "America/New_York" }));
  const local = tzDate.getTime();
  return local - utc;
}
function buildSarahHotFlashEvents(): HotFlashEvent[] {
  const rng = mulberry32(stringSeed("sarah-m-knowmeno-v2"));
  const targets: number[] = new Array(90).fill(0);
  // Phase 1 (idx 0..29 ↔ daysAgo 89..60): 664 events, jittered ~22/day
  const p1w: number[] = [];
  for (let i = 0; i < 30; i++) p1w.push(0.85 + rng() * 0.3);
  const p1Sum = p1w.reduce((s, v) => s + v, 0);
  for (let i = 0; i < 30; i++) targets[i] = Math.round((p1w[i] / p1Sum) * 664);
  targets[29] += 664 - targets.slice(0, 30).reduce((s, v) => s + v, 0);
  // Phase 2 (idx 30..59 ↔ daysAgo 59..30): 58 events, ^1.6 decay
  const p2w: number[] = [];
  for (let i = 0; i < 30; i++) p2w.push(Math.pow(1 - i / 30, 1.6));
  const p2Sum = p2w.reduce((s, v) => s + v, 0);
  for (let i = 0; i < 30; i++) targets[30 + i] = Math.round((p2w[i] / p2Sum) * 58);
  targets[30] += 58 - targets.slice(30, 60).reduce((s, v) => s + v, 0);
  // Phase 3 (idx 60..89 ↔ daysAgo 29..0): 8 events on 8 distinct days
  const days30 = Array.from({ length: 30 }, (_, i) => i);
  for (let i = days30.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [days30[i], days30[j]] = [days30[j], days30[i]];
  }
  for (let i = 0; i < 8; i++) targets[60 + days30[i]] = 1;
  // Generate events with evening-skewed hours (peak band: Late evening 8pm–12am).
  // Distribution: 65% 20-23 (band 5), 25% 16-19 (band 4), 10% else.
  const events: HotFlashEvent[] = [];
  const todayNoon = new Date();
  todayNoon.setHours(12, 0, 0, 0);
  for (let idx = 0; idx < 90; idx++) {
    const daysAgo = 89 - idx;
    const count = targets[idx];
    for (let e = 0; e < count; e++) {
      const r = rng();
      let hour: number;
      if (r < 0.10) hour = Math.floor(rng() * 16);          // 0-15
      else if (r < 0.35) hour = 16 + Math.floor(rng() * 4); // 16-19 (Evening)
      else hour = 20 + Math.floor(rng() * 4);               // 20-23 (Late evening)
      const min = Math.floor(rng() * 60);
      const dayDate = new Date(todayNoon.getTime() - daysAgo * 86400000);
      dayDate.setHours(hour, min, 0, 0);
      // Re-anchor to NY local clock — shift by (browser TZ - NY TZ).
      // Then store as ISO; consumers re-derive local via Date#getHours
      // on the viewer's machine. For deterministic peak-band rendering
      // in the founder's NY-anchored demo, this keeps "8pm event"
      // landing in band 5 regardless of where the report is opened.
      const browserOff = -dayDate.getTimezoneOffset() * 60000;
      const nyOff = nyOffsetMs(dayDate);
      const adjusted = new Date(dayDate.getTime() + (browserOff - nyOff));
      events.push({ occurred_at: adjusted.toISOString() });
    }
  }
  events.sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));
  return events;
}

function fmtLabelFromKey(key: string): string {
  return new Date(key + "T00:00:00").toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

function fmtShortFromKey(key: string): string {
  return new Date(key + "T00:00:00").toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

function keyDaysAgo(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return localDateKey(d);
}

function buildMockReportDataset(): MockBundle {
  // ---- Build the full 143-day calendar oldest → newest ----
  const allDays: { daysAgo: number; key: string; phase: Phase | "start"; logged: boolean }[] = [];
  for (let da = TRACKING_DAYS - 1; da >= 0; da--) {
    const phase = phaseFor(da);
    const logged = phase !== "start" && !SKIP_DAYS_AGO.has(da);
    allDays.push({ daysAgo: da, key: keyDaysAgo(da), phase, logged });
  }

  // ---- Build per-symptom severity vectors per phase ----
  // Group logged days by phase (in chronological order within phase).
  const symList = Object.keys(PLAN);
  const loggedByPhase: Record<Phase, typeof allDays> = {
    pre: allDays.filter(d => d.phase === "pre" && d.logged),
    post: allDays.filter(d => d.phase === "post" && d.logged),
    middle: allDays.filter(d => d.phase === "middle" && d.logged),
    prior30: allDays.filter(d => d.phase === "prior30" && d.logged),
    last30: allDays.filter(d => d.phase === "last30" && d.logged),
  };
  // Sanity: bucket totals must match logged-day counts per phase.
  // (If counts ever drift, adjust PLAN — flag math depends on exact counts.)
  const ALL_PHASES: Phase[] = ["pre", "post", "middle", "prior30", "last30"];
  for (const sym of symList) {
    for (const phase of ALL_PHASES) {
      const b = PLAN[sym][phase];
      const sum = b.sev1 + b.sev2 + b.sev3 + (b.sev4 || 0);
      const need = loggedByPhase[phase].length;
      if (sum !== need) {
        console.warn(`[mock] ${sym}/${phase} bucket sum ${sum} ≠ logged days ${need}`);
      }
    }
  }

  const logsByDate: Record<string, Record<string, number>> = {};
  symList.forEach((sym, symIdx) => {
    for (const phase of ALL_PHASES) {
      const seq = planSequence(PLAN[sym][phase], symIdx * 7);
      const days = loggedByPhase[phase];
      for (let i = 0; i < days.length; i++) {
        const k = days[i].key;
        if (!logsByDate[k]) logsByDate[k] = {};
        logsByDate[k][sym] = seq[i];
      }
    }
  });

  // ---- Build mood rows for last 90 days (89 logged) ----
  // Sarah trends slightly low overall, with a clear "settled" second
  // half — drives a green-tinted trend without being saccharine.
  // Mood scale: 0 best (😄) … 4 worst (😩).
  const moodRows: { log_date: string; mood: number }[] = [];
  for (let da = 89; da >= 0; da--) {
    if (SKIP_DAYS_AGO.has(da)) continue;
    // First half (da 89..45): mostly 2-3 with occasional 1.
    // Second half (da 44..0): mostly 1-2 with occasional 0.
    const inLate = da <= 44;
    const mod = (89 - da) % 5;
    let mood: number;
    if (inLate) {
      mood = mod === 0 ? 0 : mod === 4 ? 2 : 1;
    } else {
      mood = mod === 0 ? 1 : mod === 4 ? 3 : 2;
    }
    moodRows.push({ log_date: keyDaysAgo(da), mood });
  }

  // ---- Build HRT data ----
  // HRT logged every day from start (daysAgo 112) → today, minus the
  // single skip day. ~88% adherence in last 30: 25 taken / 4 missed (29 logged).
  const hrtByDate: Record<string, boolean> = {};
  const hrtDates90: string[] = [];
  // last-30 window (used by adherence calc)
  // Missed days (deterministic): daysAgo 27, 19, 9, 3 → 4 missed in 29 logged.
  const MISSED_LAST30 = new Set<number>([27, 19, 9, 3]);
  for (let da = 29; da >= 0; da--) {
    if (SKIP_DAYS_AGO.has(da)) continue;
    hrtByDate[keyDaysAgo(da)] = !MISSED_LAST30.has(da);
  }
  // last-90 window for confidence: every day from HRT start through today
  // that isn't a skip day. (Last 90 = daysAgo 0..89; HRT started daysAgo 112,
  // so every day in last 90 is post-HRT-start.)
  for (let da = 89; da >= 0; da--) {
    if (SKIP_DAYS_AGO.has(da)) continue;
    hrtDates90.push(keyDaysAgo(da));
  }

  // ---- Build ReportData (matches fetchReportData return shape) ----
  const trackingSinceKey = keyDaysAgo(TRACKING_DAYS - 1);
  const loggedDates90 = Object.keys(logsByDate)
    .filter(k => {
      // Only keep last-90 dates for loggedDates90 / loggedDays90.
      const d = new Date(k + "T00:00:00").getTime();
      const today = new Date(keyDaysAgo(0) + "T00:00:00").getTime();
      const day89 = new Date(keyDaysAgo(89) + "T00:00:00").getTime();
      return d >= day89 && d <= today;
    })
    .sort();

  const reportData: ReportData = {
    trackingSinceKey,
    trackingSinceLabel: fmtLabelFromKey(trackingSinceKey),
    milestoneDay: TRACKING_DAYS, // 143
    loggedDays90: loggedDates90.length, // 89
    loggedDates90,
    logsByDate,
  };

  // ---- Cycle data: 6 cycles, mostly 28-30d ----
  // Anchored to keyDaysAgo so they drift with "today" each invocation.
  // Most recent first to match app convention.
  const cycles = [
    { start: fmtShortFromKey(keyDaysAgo(8)),   cycle: 29, days: 4, flow: "Light"  },
    { start: fmtShortFromKey(keyDaysAgo(37)),  cycle: 30, days: 4, flow: "Medium" },
    { start: fmtShortFromKey(keyDaysAgo(67)),  cycle: 28, days: 5, flow: "Medium" },
    { start: fmtShortFromKey(keyDaysAgo(95)),  cycle: 29, days: 5, flow: "Heavy"  },
    { start: fmtShortFromKey(keyDaysAgo(124)), cycle: 30, days: 5, flow: "Heavy"  },
    { start: fmtShortFromKey(keyDaysAgo(140)), cycle: 28, days: 6, flow: "Heavy"  },
  ];

  // ---- Lab uploads (3 timepoints across the tracking window) ----
  const labs = [
    {
      date: fmtShortFromKey(keyDaysAgo(140)), daysAgo: 140,
      estradiol: { value: 22, unit: "pg/mL" },
      fsh:       { value: 48, unit: "mIU/mL" },
      lh:        { value: 32, unit: "mIU/mL" },
      progesterone: { value: 0.4, unit: "ng/mL" },
      tsh:       { value: 2.1, unit: "µIU/mL" },
    },
    {
      date: fmtShortFromKey(keyDaysAgo(75)), daysAgo: 75,
      estradiol: { value: 58, unit: "pg/mL" },
      fsh:       { value: 41, unit: "mIU/mL" },
      lh:        { value: 28, unit: "mIU/mL" },
      progesterone: { value: 0.5, unit: "ng/mL" },
      tsh:       { value: 2.3, unit: "µIU/mL" },
    },
    {
      date: fmtShortFromKey(keyDaysAgo(10)), daysAgo: 10,
      estradiol: { value: 76, unit: "pg/mL" },
      fsh:       { value: 36, unit: "mIU/mL" },
      lh:        { value: 24, unit: "mIU/mL" },
      progesterone: { value: 0.6, unit: "ng/mL" },
      tsh:       { value: 2.2, unit: "µIU/mL" },
    },
  ];

  // ---- User profile ----
  // Override hrtStartDate to keyDaysAgo(30) so the hot-flash narrative
  // (HRT started 30 days ago → sharp drop in events) reads cleanly.
  // Non-hot-flash severity PLAN buckets are intentionally NOT rebalanced
  // here (B1 decision) — buildHrtResponse for severity symptoms now
  // compares day -59..-30 vs day -29..0 (essentially identical PLAN
  // buckets), so other symptoms render as "all stable post-HRT". That
  // is the credible "hot flashes responded fast, other symptoms still
  // being tracked" demo narrative.
  const hrtStartKey = keyDaysAgo(30);
  const user = {
    firstName: "Sarah",
    lastName: "M.",
    name: "Sarah M.",
    email: "sarah.m@example.com",
    dob: "1975-06-15",
    stage: "Late perimenopause",
    lifeStage: "Late perimenopause",
    hrtType: "Estradiol patch",
    hrtTypes: ["Estradiol patch"],
    hrtDose: "0.05 mg twice weekly",
    hrtSchedule: "twice-weekly",
    hrtStartDate: hrtStartKey,
    otherMeds: "",
    otherConditions: "",
    healthProfileComplete: true,
    isPremium: true,
    recognizedSymptoms: [
      "Hot flashes",
      "Night sweats",
      "Brain fog",
      "Mood swings",
      "Fatigue",
    ],
  };

  const questions = [
    "Hot flashes dropped from ~22/day to a handful per week after starting HRT — does the residual evening cluster suggest we need a higher dose or a different delivery?",
    "Other symptoms (brain fog, mood swings, fatigue) are still tracking moderate severity 30 days in — is that a reasonable response timeline or worth escalating?",
    "When should we retest hormone levels given the strong vasomotor response?",
  ];

  const hotFlashEvents = buildSarahHotFlashEvents();

  return {
    user,
    cycles,
    labs,
    questions,
    reportData,
    hrtByDate,
    hrtDates90,
    moodRows,
    hotFlashEvents,
  };
}

export const Route = createFileRoute("/dev/sample-report")({
  component: SampleReportPage,
});

function SampleReportPage() {
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      setEmail(data.user?.email ?? null);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return <Shell>Loading…</Shell>;
  }

  if (!DEV_TOOLS_ENABLED) {
    return <Shell>403 — dev tools disabled.</Shell>;
  }

  if (!email || !FOUNDER_EMAIL_RE.test(email)) {
    return (
      <Shell>
        403 — not authorised.
        {email && (
          <div style={{ fontSize: 12, color: "#888", marginTop: 8 }}>
            Signed in as {email}
          </div>
        )}
      </Shell>
    );
  }

  const handleGenerate = async () => {
    const bundle = buildMockReportDataset();
    const clean = new URLSearchParams(window.location.search).get("clean") === "1";
    await generateReportPDF(bundle.user, TRACKING_DAYS, bundle.cycles, {
      hasWearable: false,
      labUploads: bundle.labs,
      patientQuestions: bundle.questions,
      clean,
      mockOverrides: {
        reportData: bundle.reportData,
        hrtByDate: bundle.hrtByDate,
        hrtDates90: bundle.hrtDates90,
        moodRows: bundle.moodRows,
        hotFlashEvents: bundle.hotFlashEvents,
      },
    });
  };

  return (
    <Shell>
      <h1 style={{ fontSize: 22, fontWeight: 500, margin: "0 0 8px" }}>
        Sample Symptom Intelligence Report
      </h1>
      <p style={{ color: "#6b6a64", fontSize: 14, lineHeight: 1.55, margin: "0 0 18px" }}>
        Generates the exact Day-143 Symptom Intelligence Report PDF a real user would
        receive, populated with synthetic data for{" "}
        <strong style={{ color: "#2b2b28", fontWeight: 500 }}>Sarah M.</strong>{" "}
        — 50, late perimenopause, started estradiol 0.05 mg patch on
        Day 31. 89 of last 90 days logged → HIGH data confidence.
        Hot flashes / night sweats / brain fog / fatigue all responded;
        mood swings worsened post-HRT → fires the "HRT non-response" flag.
      </p>
      <button
        onClick={handleGenerate}
        style={{
          padding: "12px 22px",
          borderRadius: 12,
          background: "#1D9E75",
          color: "#fff",
          border: "none",
          fontSize: 14,
          fontWeight: 500,
          cursor: "pointer",
        }}
      >
        Generate sample report (PDF preview) →
      </button>
      <p style={{ fontSize: 12, color: "#888", marginTop: 18, lineHeight: 1.55 }}>
        Opens in a new window. Use your browser's <em>Save as PDF</em> from the
        print dialog to download. This is the same template/code path as the
        production user PDF — synthetic data is injected via opts.mockOverrides
        so no real account is touched.
      </p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#FAF8F3",
        padding: "60px 24px",
        fontFamily: "-apple-system, system-ui, sans-serif",
        color: "#2b2b28",
      }}
    >
      <div
        style={{
          maxWidth: 560,
          margin: "0 auto",
          background: "#fff",
          padding: "32px 28px",
          borderRadius: 18,
          border: "1px solid #e8e6df",
        }}
      >
        {children}
      </div>
    </div>
  );
}
